// Downloading the quote PDF, shared by the places that offer it.
//
// The PDF is built server-side by an external service rendering the customer-facing offer
// page, so a request takes seconds rather than milliseconds - every caller needs to show
// that something is happening and to cope with it failing.

import axios from 'axios';
import readableId from '../../ReadableId.js';


// The name the quote carries, in the attachment row and on the downloaded file alike, so
// what is shown and what lands in the customer's folder are the same string.
//
// Built from the opportunity's Transportation, Routes and Incoterm - method, incoterm and
// the route - plus the readable quote number, which is what makes it unique.
function quotePdfFilename(opportunity) {
    if (!opportunity) return 'Quote.pdf';

    const routes = [
        ...(opportunity.routes || []),
        ...((opportunity.offers || []).flatMap(offer => offer.routes || [])),
    ].filter(r => r && (r.source || r.destination));

    const first = routes[0];
    const route = first
        ? `${first.source?.text || 'Anywhere'} to ${first.destination?.text || 'Anywhere'}`
        : '';
    // A quote can price several lanes; naming them all would be unreadable.
    const more = routes.length > 1 ? ` +${routes.length - 1}` : '';

    const method = opportunity.transportationMethod
        ? opportunity.transportationMethod.charAt(0).toUpperCase() + opportunity.transportationMethod.slice(1)
        : '';

    // Joined with spaces rather than dashes: a port already reads "DEHAM - Hamburg", and
    // separating the parts with dashes too turns the name into a row of them. The quote
    // number goes in brackets at the end so it stays easy to pick out.
    const described = [method, opportunity.incoterm, route ? route + more : ''].filter(Boolean).join(' ');
    const name = `${described} (${readableId(opportunity)})`.trim();
    // Strip what a filesystem will not take, and collapse the gaps that leaves behind.
    return `${name.replace(/[/\\:*?"<>|]/g, ' ').replace(/\s+/g, ' ').trim()}.pdf`;
}

/**
 * Fetch the quote's PDF and hand it to the browser as a download.
 *
 * The endpoint returns the file as base64 in a JSON body rather than as a binary response,
 * so it is decoded here and handed over as a blob; pointing a link straight at the URL
 * would download the JSON envelope instead of the PDF.
 *
 * @returns {Promise<string>} the filename that was downloaded
 */
async function downloadQuotePdf({ opportunityId, accesstoken, minimized = false, filename }) {
    const { data } = await axios.get(
        `/api/opportunities/${opportunityId}/pdf?accesstoken=${accesstoken}${minimized ? '&minimized=true' : ''}`
    );
    if (!data?.content) throw new Error('The server returned no PDF content.');

    const binary = window.atob(data.content);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);

    const blob = new Blob([bytes], { type: data.type || 'application/pdf' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    // Prefer the caller's name so the download matches what the screen showed.
    link.download = filename || data.filename || 'quote.pdf';
    document.body.appendChild(link);
    link.click();
    link.remove();
    // Let the click start before releasing the blob, or the download can be cancelled.
    window.setTimeout(() => window.URL.revokeObjectURL(url), 1000);

    return filename || data.filename || 'quote.pdf';
}

export { downloadQuotePdf, quotePdfFilename };
